import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  Calendar,
  Users,
  Settings,
  LogOut,
  ArrowLeft,
  Home,
  DollarSign,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Plus,
  ChevronRight,
  Menu,
  X,
  RefreshCw,
  MapPin,
  Phone,
  Mail,
  BarChart3,
  TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentBuildingWorkspace() {
  const { hoaId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [building, setBuilding] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentNotices, setRecentNotices] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    if (hoaId) {
      fetchBuildingData();
    }
  }, [hoaId]);

  const fetchBuildingData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/managing-agents/building/${hoaId}/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBuilding(data.building);
        setStats(data.stats);
        setRecentTickets(data.recent_tickets || []);
        setRecentNotices(data.recent_notices || []);
      } else {
        // Demo data
        setBuilding({
          hoa_id: hoaId,
          name: 'Demo Building',
          country: 'South Africa',
          address: '123 Main Street, Cape Town',
          total_units: 48,
        });
        setStats({
          open_tickets: 5,
          total_units: 48,
          total_residents: 92,
        });
        setRecentTickets([
          { ticket_id: '1', title: 'Elevator maintenance', status: 'pending', priority: 'high', created_at: new Date().toISOString() },
          { ticket_id: '2', title: 'Pool pump repair', status: 'in_progress', priority: 'medium', created_at: new Date().toISOString() },
        ]);
        setRecentNotices([
          { notice_id: '1', title: 'Water maintenance scheduled', category: 'maintenance', created_at: new Date().toISOString() },
        ]);
      }
    } catch (error) {
      console.error('Error fetching building data:', error);
      toast.error('Failed to load building data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-sky-100 text-sky-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-red-100 text-red-700',
      urgent: 'bg-red-500 text-white',
    };
    return styles[priority] || styles.medium;
  };

  const sidebarItems = [
    { icon: BarChart3, label: 'Overview', tab: 'overview' },
    { icon: Wrench, label: 'Tickets', tab: 'tickets' },
    { icon: Bell, label: 'Notices', tab: 'notices' },
    { icon: FileText, label: 'Documents', tab: 'documents' },
    { icon: Calendar, label: 'Venue Bookings', tab: 'venue_bookings', navigate_to: `/agent/building/${hoaId}/venue-bookings` },
    { icon: DollarSign, label: 'Financials', tab: 'financials' },
    { icon: TrendingUp, label: 'Analytics', tab: 'analytics' },
    { icon: Users, label: 'Residents', tab: 'residents' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="agent-building-workspace">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-indigo-900 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-indigo-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white text-sm truncate max-w-[140px]">{building?.name || 'Building'}</p>
                <p className="text-xs text-indigo-300">Workspace</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden text-indigo-300" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Back to Dashboard */}
          <div className="p-3 border-b border-indigo-800">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-indigo-300 hover:text-white hover:bg-indigo-800"
              onClick={() => navigate('/agent')}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Buildings
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.tab}>
                  <button
                    onClick={() => item.navigate_to ? navigate(item.navigate_to) : setActiveTab(item.tab)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      activeTab === item.tab
                        ? 'bg-indigo-500 text-white'
                        : 'text-indigo-200 hover:text-white hover:bg-indigo-800'
                    }`}
                    data-testid={`tab-${item.tab}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-indigo-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Agent'}</p>
                <p className="text-xs text-indigo-300 truncate">Managing Agent</p>
              </div>
              <Button variant="ghost" size="icon" className="text-indigo-300 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-indigo-900">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" className="text-white" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold text-white truncate max-w-[200px]">{building?.name}</span>
          <Button variant="ghost" size="icon" className="text-white" onClick={fetchBuildingData}>
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-7xl mx-auto">
          {loading ? (
            <div className="space-y-4">
              <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Building Header */}
              <div className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">{building?.name}</h1>
                <div className="flex items-center gap-4 mt-2 text-slate-500">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{building?.address || building?.country}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Home className="w-4 h-4" />
                    <span className="text-sm">{building?.total_units || stats?.total_units || 0} units</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">Open Tickets</p>
                        <p className="text-3xl font-bold">{stats?.open_tickets || 0}</p>
                      </div>
                      <Wrench className="w-10 h-10 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-emerald-100 text-sm">Total Units</p>
                        <p className="text-3xl font-bold">{stats?.total_units || building?.total_units || 0}</p>
                      </div>
                      <Home className="w-10 h-10 text-emerald-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-sky-500 to-blue-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sky-100 text-sm">Residents</p>
                        <p className="text-3xl font-bold">{stats?.total_residents || 0}</p>
                      </div>
                      <Users className="w-10 h-10 text-sky-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-violet-500 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Notices</p>
                        <p className="text-3xl font-bold">{recentNotices.length}</p>
                      </div>
                      <Bell className="w-10 h-10 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Recent Tickets */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-lg">Recent Tickets</CardTitle>
                      <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => setActiveTab('tickets')}>
                        View All
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {recentTickets.length === 0 ? (
                        <p className="text-slate-500 text-center py-4">No tickets yet</p>
                      ) : (
                        <div className="space-y-3">
                          {recentTickets.slice(0, 5).map(ticket => (
                            <div key={ticket.ticket_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">{ticket.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={getStatusColor(ticket.status)}>
                                    {ticket.status?.replace('_', ' ')}
                                  </Badge>
                                  <Badge className={getPriorityBadge(ticket.priority)}>
                                    {ticket.priority}
                                  </Badge>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent Notices */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-lg">Recent Notices</CardTitle>
                      <Button variant="ghost" size="sm" className="text-indigo-600" onClick={() => setActiveTab('notices')}>
                        View All
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {recentNotices.length === 0 ? (
                        <p className="text-slate-500 text-center py-4">No notices yet</p>
                      ) : (
                        <div className="space-y-3">
                          {recentNotices.slice(0, 5).map(notice => (
                            <div key={notice.notice_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 truncate">{notice.title}</p>
                                <p className="text-sm text-slate-500">{notice.category}</p>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'tickets' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Maintenance Tickets</CardTitle>
                    <Button 
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate(`/agent/building/${hoaId}/tickets`)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Manage Tickets
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {recentTickets.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No tickets found</p>
                    ) : (
                      <div className="space-y-3">
                        {recentTickets.map(ticket => (
                          <div key={ticket.ticket_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{ticket.title}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={getStatusColor(ticket.status)}>
                                  {ticket.status?.replace('_', ' ')}
                                </Badge>
                                <Badge className={getPriorityBadge(ticket.priority)}>
                                  {ticket.priority}
                                </Badge>
                                <span className="text-sm text-slate-500">
                                  {new Date(ticket.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/agent/building/${hoaId}/tickets`)}
                            >
                              View Details
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-center mt-4">
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/agent/building/${hoaId}/tickets`)}
                      >
                        View All Tickets
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'notices' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Building Notices</CardTitle>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => navigate(`/admin/notices?building=${hoaId}`)} data-testid="agent-create-notice-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Notice
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {recentNotices.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No notices found</p>
                    ) : (
                      <div className="space-y-3">
                        {recentNotices.map(notice => (
                          <div key={notice.notice_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{notice.title}</p>
                              <p className="text-sm text-slate-500 mt-1">{notice.category}</p>
                            </div>
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {activeTab === 'documents' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Building Documents</CardTitle>
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate(`/admin/documents?building=${hoaId}`)}
                      data-testid="agent-manage-documents-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Manage Documents
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">Upload, organise and share documents (rules, financials, AGM minutes, contracts and more) with residents and trustees.</p>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/admin/documents?building=${hoaId}`)}
                      className="w-full"
                      data-testid="agent-open-documents-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Open Document Vault
                    </Button>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'financials' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Financial Documents</CardTitle>
                    <Button 
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate(`/agent/building/${hoaId}/financials`)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Manage Financials
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-500 text-center py-4">Upload levy statements, bank statements, and other financial documents for trustees.</p>
                    <div className="flex justify-center">
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/agent/building/${hoaId}/financials`)}
                      >
                        Go to Financial Documents
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'residents' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Building Residents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-500 text-center py-8">Resident directory coming soon</p>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'analytics' && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Levy & Expense Analytics</CardTitle>
                    <Button 
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => navigate(`/agent/building/${hoaId}/analytics`)}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Full Analytics
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-500 text-center py-4">
                      Access maintenance cost benchmarking, contractor price comparisons, levy forecasting, and reserve fund planning insights.
                    </p>
                    <div className="flex justify-center">
                      <Button 
                        variant="outline"
                        onClick={() => navigate(`/agent/building/${hoaId}/analytics`)}
                      >
                        Go to Analytics Engine
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
